import type { UIMessage } from "ai";
import {
  isCarouselSocialPostType,
  standaloneAssetGenerationPostTypeSchema,
  standaloneAssetBatchCreateRequestV1Schema,
  type StandaloneAssetBatchCreateRequestV1,
  type SocialPostType,
} from "@runcheetah/shared";

export type AssistantStandaloneAssetBatchPreviewReference = {
  id: string;
  summary: string | null;
  assetCount: number;
  hasExplicitAssetCount: boolean;
  formats: SocialPostType[];
  firstScheduledFor: string | null;
  scheduleSpacingHours: number | null;
  request: StandaloneAssetBatchCreateRequestV1;
};

export function isAssistantStandaloneCarouselRequest(
  request: StandaloneAssetBatchCreateRequestV1,
): boolean {
  return request.items.length === 1 && isCarouselSocialPostType(request.items[0]!.postType);
}

export function getAssistantStandaloneAssetBatchPreviewReferences(
  message: Pick<UIMessage, "parts">,
): AssistantStandaloneAssetBatchPreviewReference[] {
  const references: AssistantStandaloneAssetBatchPreviewReference[] = [];

  for (const [index, part] of message.parts.entries()) {
    const candidate = part as {
      type?: string;
      state?: string;
      toolCallId?: string;
      output?: unknown;
    };

    if (
      candidate.type !== "tool-preview_standalone_asset_batch_creation" ||
      candidate.state !== "output-available"
    ) {
      continue;
    }

    const reference = parseAssistantStandaloneAssetBatchPreviewReference(
      candidate.output,
      typeof candidate.toolCallId === "string" ? candidate.toolCallId : `asset-batch-${index}`,
    );
    if (reference) {
      references.push(reference);
    }
  }

  return references;
}

export function isAssistantStandaloneAssetBatchProposalStale(
  messages: readonly Pick<UIMessage, "id" | "role">[],
  proposalMessageId: string,
) {
  const proposalMessageIndex = messages.findIndex((message) => message.id === proposalMessageId);
  if (proposalMessageIndex === -1) {
    return false;
  }

  return messages.slice(proposalMessageIndex + 1).some((message) => message.role === "user");
}

function parseAssistantStandaloneAssetBatchPreviewReference(
  value: unknown,
  id: string,
): AssistantStandaloneAssetBatchPreviewReference | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const output = value as {
    render?: { component?: unknown };
    summary?: unknown;
    assetCount?: unknown;
    hasExplicitAssetCount?: unknown;
    formats?: unknown;
    firstScheduledFor?: unknown;
    scheduleSpacingHours?: unknown;
    request?: unknown;
  };

  if (output.render?.component !== "standalone_asset_batch_confirmation") {
    return null;
  }

  const request = standaloneAssetBatchCreateRequestV1Schema.safeParse(output.request);
  if (!request.success) {
    return null;
  }

  if (
    request.data.items.some(
      (item) => !standaloneAssetGenerationPostTypeSchema.safeParse(item.postType).success,
    )
  ) {
    return null;
  }

  const formats = Array.isArray(output.formats)
    ? output.formats.filter((format): format is SocialPostType =>
        request.data.items.some((item) => item.postType === format),
      )
    : request.data.items.map((item) => item.postType);

  return {
    id,
    summary:
      typeof output.summary === "string" && output.summary.trim() ? output.summary.trim() : null,
    assetCount:
      typeof output.assetCount === "number" && Number.isInteger(output.assetCount)
        ? output.assetCount
        : request.data.items.length,
    hasExplicitAssetCount: output.hasExplicitAssetCount === true,
    formats,
    firstScheduledFor:
      typeof output.firstScheduledFor === "string"
        ? output.firstScheduledFor
        : (request.data.items[0]?.scheduledFor ?? null),
    scheduleSpacingHours:
      typeof output.scheduleSpacingHours === "number" &&
      Number.isInteger(output.scheduleSpacingHours)
        ? output.scheduleSpacingHours
        : null,
    request: request.data,
  };
}
